import { Avatar } from '@creditornot/cb-avatar';
import { TextButtonLink } from '@creditornot/cb-button';
import { colors, cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { useDirection } from '@creditornot/cb-provider';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { areEqual } from 'react-window';
import styled from 'styled-components';

import { WideAreaCheckbox } from 'common/components';
import { localize } from 'common/utils';
import { VenueWithSharedMenuLinkInfo } from 'modules/shared-menu/hooks/useVenuesWithSelectionLinks';

export type VenueRowData = {
  onRowClick: (id: string) => void;
  selectedVenueIds: Set<string>;
  venues: VenueWithSharedMenuLinkInfo[];
};

type Props = {
  index: number;
  data: VenueRowData;
  style: React.CSSProperties;
};

const Root = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  cursor: pointer;
  width: 100vw;
  height: 100%;
  padding-inline-end: 24px;

  background-color: ${({ selected }) => {
    if (selected) {
      return colors.wolt4;
    }
    return cbSemanticCssVars.surfaceMain;
  }};

  &:hover {
    background-color: ${({ selected }) =>
      selected ? colors.wolt8 : cssThemedValue({ light: colors.pepper8, dark: colors.salt4 })};
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
`;
const Right = styled.div``;

const VenueNameAndAvatar = styled.div`
  display: flex;
  align-items: center;
`;

const ImageContainer = styled.div`
  margin-inline-end: 12px;
  display: flex;
  align-items: center;

  img {
    object-fit: cover;
  }
`;

const VenueCity = styled.span`
  ${typographyCss.Small()}
`;

const VenueListRowRenderer = ({ index, data: { onRowClick, selectedVenueIds, venues }, style }: Props) => {
  const cbDirection = useDirection();
  const intl = useIntl();

  const venue = venues[index];

  const selected = selectedVenueIds.has(venue.id);

  const venuePageUrl = useMemo(() => `${window.location.origin}/venue/${venue.slug}`, [venue.slug]);

  return (
    <Root dir={cbDirection} style={style} selected={selected} onClick={() => onRowClick(venue.id)}>
      <Left>
        <WideAreaCheckbox
          onToggle={() => onRowClick(venue.id)}
          selectionStatus={selected ? 'selected' : 'not-selected'}
        />
        <VenueNameAndAvatar>
          <ImageContainer>
            <Avatar size="tiny" imgSrc={venue.listimage || undefined} />
          </ImageContainer>
          <TextButtonLink target="_blank" href={venuePageUrl} onClick={e => e.stopPropagation()}>
            {localize(venue.name, intl.locale)}
          </TextButtonLink>
        </VenueNameAndAvatar>
      </Left>
      <Right>
        <VenueCity>{venue.city}</VenueCity>
      </Right>
    </Root>
  );
};

export default React.memo(VenueListRowRenderer, areEqual);
